import React from 'react'

import { Helmet } from 'react-helmet'

import TopMenu from '../components/top-menu'
import Footer from '../components/footer'
import './snapframe.css'

const snapframeAssets = {
  layouts: '/Projects/Snapframe/Snapframe%201%20-%20Layouts.png',
  navigator: '/Projects/Snapframe/Snapframe%202%20-%20HUD.png',
  bars: '/Projects/Snapframe/Snapframe%203%20-%20Snap%20Bars.png',
  icon: '/Projects/Snapframe/Snapframe%204%20-%20Icon.png',
}

const Snapframe = () => {
  return (
    <div className="snapframe-page">
      <Helmet>
        <title>SnapFrame macOS App Case Study | Vlad Butucariu</title>
        <meta
          name="description"
          content="Product design and native development for SnapFrame, a context-aware macOS window manager for custom layouts, developer workflows, and AI tools."
        />
        <meta
          property="og:title"
          content="SnapFrame macOS App Case Study | Vlad Butucariu"
        />
        <meta
          property="og:description"
          content="Designing and building a native macOS workspace tool that combines custom window layouts with live developer and AI context."
        />
        <meta
          property="og:image"
          content="https://vladbutucariu.com/Projects/Snapframe/Snapframe%201%20-%20Layouts.png"
        />
        <link rel="canonical" href="https://vladbutucariu.com/snapframe" />
        <meta property="og:url" content="https://vladbutucariu.com/snapframe" />
      </Helmet>

      <div className="snapframe-wrapper">
        <TopMenu text3="Capabilities"></TopMenu>

        <header className="snapframe-hero">
          <div className="snapframe-video" aria-hidden="true">
            <video
              src="/Video/irreversible-blob-bg-1780402436590.mp4"
              loop
              muted
              autoPlay
              playsInline
            ></video>
          </div>

          <div className="snapframe-hero-shell project-page-shell">
            <div className="snapframe-intro project-page-intro">
              <h1 className="snapframe-title header-project">
                SnapFrame — Workspace Manager
              </h1>

              <div className="snapframe-summary">
                <h2>
                  SnapFrame is a native macOS workspace tool I am designing and
                  building to combine custom window layouts, keyboard-driven
                  navigation, and live context for developer and AI-assisted
                  workflows.
                </h2>

                <div className="snapframe-tags" aria-label="Project disciplines">
                  <span className="tag-label-big">macos app</span>
                  <span className="tag-label-big">ux/ui</span>
                  <span className="tag-label-big">ai tools</span>
                </div>
              </div>

              <div className="snapframe-overview">
                <div className="snapframe-overview-row">
                  <span className="snapframe-overview-label">Context</span>
                  <p>
                    Conventional window managers stop after arranging windows.
                    That is limiting for developers who move between browsers,
                    terminals, design tools, builds, and several AI agents at
                    once. SnapFrame treats the desktop as one coordinated
                    workspace instead of a set of unrelated rectangles.
                  </p>
                </div>

                <div className="snapframe-overview-row">
                  <span className="snapframe-overview-label">Product</span>
                  <p>
                    Custom grids and Fusion Layouts group related apps, while a
                    keyboard-driven navigator makes saved workspaces quick to
                    preview and apply. Sticky resizing keeps grouped windows in
                    proportion as the workspace changes.
                  </p>
                </div>

                <div className="snapframe-overview-row">
                  <span className="snapframe-overview-label">Build</span>
                  <p>
                    The working app is built natively in Swift and AppKit. It
                    uses macOS Accessibility APIs to coordinate windows and
                    attach optional status bars that can show app performance,
                    AI usage, and task state without interrupting the underlying
                    application.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="snapframe-gallery">
          <div
            className="snapframe-gallery-transition"
            aria-hidden="true"
          ></div>
          <div className="snapframe-gallery-shell project-page-shell">
            <figure className="snapframe-feature">
              <img
                src={snapframeAssets.layouts}
                alt="SnapFrame Fusion Layouts grouping multiple macOS applications into a coordinated workspace"
              />
              <figcaption>
                <strong>Coordinated layouts.</strong> Custom grids can combine
                several applications into one reusable workspace, with linked
                resizing that preserves the intended composition.
              </figcaption>
            </figure>

            <figure className="snapframe-feature">
              <img
                src={snapframeAssets.navigator}
                alt="SnapFrame keyboard-driven heads-up display for previewing and selecting saved window layouts"
                loading="lazy"
              />
            </figure>

            <figure className="snapframe-feature">
              <img
                src={snapframeAssets.bars}
                alt="SnapFrame status bars showing app identity, performance data, and AI usage above managed windows"
                loading="lazy"
              />
              <figcaption>
                <strong>Context on the window itself.</strong> Optional,
                click-through Snap Bars surface CPU and memory data, AI quotas,
                and workflow status exactly where that information is relevant.
              </figcaption>
            </figure>

            <figure className="snapframe-feature snapframe-feature-icon">
              <img
                src={snapframeAssets.icon}
                alt="SnapFrame application icon shown in the macOS Dock"
                loading="lazy"
              />
              <figcaption>
                <strong>A complete independent product.</strong> Product
                definition, interaction design, visual identity, native
                implementation, and continuous testing are being developed as
                one connected system.
              </figcaption>
            </figure>
          </div>
        </main>

        <Footer rootClassName="footerroot-class-name2"></Footer>
      </div>
    </div>
  )
}

export default Snapframe
